<template>
  <div v-if="items.length > 0" class="invitation-group mb-4 position-relative">
    <div class="invitation-group__head d-flex align-items-center justify-content-between mb-3">
      <h5 class="invitation-group__title mb-0">{{ title }}</h5>
      <span class="badge badge-soft">{{ items.length }}</span>
    </div>

    <article
      v-for="event in items"
      :key="event.id"
      class="invite-card gradient-card"
      :class="{ 'dropdown-open': openId === event.id }"
      role="region"
      :aria-label="readableTitle(event)"
    >
      <div class="invite-card__header">
        <div>
          <p v-if="event.host_name" class="eyebrow mb-1">
            {{ event.host_name }} added you as a Co-Host
          </p>
          <h3 class="event-title text-truncate mb-1">
            {{ truncate(readableTitle(event), 100) }}
          </h3>
        </div>

        <div class="dropdown invite-dropdown" :class="{ show: openId === event.id }" :data-id="event.id">
          <button
            class="btn btn-light p-0 rounded-circle dropbutton"
            type="button"
            :id="`menu-${event.id}`"
            aria-haspopup="true"
            :aria-expanded="openId === event.id ? 'true' : 'false'"
            :aria-controls="`menu-pop-${event.id}`"
            @click.stop="toggleMenu(event.id)"
            title="More actions"
          >
            <i class="fas fa-ellipsis-v"></i>
          </button>

          <div
            v-if="openId === event.id && !isCompactScreen"
            class="dropdown-menu show"
            role="menu"
            tabindex="-1"
            @click.stop
          >
            <template v-if="sectionType === 'active' || sectionType === 'upcoming'">
              <router-link :to="`/cohost/${event.id}`" v-if="event.cohost_add_status == 1" class="dropdown-item" role="menuitem" @click="closeMenu">
                <i class="fas fa-user-tie"></i><span>Add Co-Host</span>
              </router-link>
              <router-link :to="`/guest/${event.id}`" class="dropdown-item" role="menuitem" @click="closeMenu">
                <i class="fas fa-users"></i><span>Add Guests</span>
              </router-link>
              <router-link :to="`/edit-invitation/${event.id}`" class="dropdown-item" role="menuitem" @click="closeMenu">
                <i class="fas fa-edit"></i><span>Edit Invitation</span>
              </router-link>
            </template>

            <template v-else-if="sectionType === 'past' || sectionType === 'draft'">
              <router-link :to="`/edit-invitation/${event.id}`" class="dropdown-item" v-if="sectionType === 'draft' && event.edit_option == 'yes'" role="menuitem" @click="closeMenu">
                <i class="fas fa-edit"></i><span>Edit Invitation</span>
              </router-link>
            </template>

            <router-link 
              v-if="event.event_type === 'old'" 
              :to="`/guest/${event.id}`" 
              class="dropdown-item" 
              role="menuitem" 
              @click="closeMenu"
            >
              <i class="fas fa-users"></i><span>Add Guests</span>
            </router-link>

            <router-link v-if="sectionType !== 'past'" :to="`/send-enquiry/${event.id}`" class="dropdown-item" role="menuitem" @click="closeMenu">
              <i class="fas fa-paper-plane"></i><span>Send Enquiry</span>
            </router-link>

            <router-link
              v-if="shouldShowFeedback(event)"
              :to="`/feedbacks/${event.id}`"
              class="dropdown-item"
              role="menuitem"
              @click="closeMenu"
            >
              <i class="fas fa-comment-alt"></i><span>Feedbacks</span>
            </router-link>

            <template v-if="sectionType === 'upcoming' || sectionType === 'active'">
              <div class="dropdown-divider"></div>
              <button class="dropdown-item" role="menuitem" type="button" @click="openShareVia(event)">
                <i class="far fa-share-square"></i><span>Share Via</span>
              </button>
              <button class="dropdown-item" role="menuitem" type="button" @click="openShareQr(event)">
                <i class="fas fa-qrcode"></i><span>Album QR Code</span>
              </button>
            </template>

            <template v-if="canDeleteEvent(event)">
              <div class="dropdown-divider"></div>
              <button class="dropdown-item text-danger" role="menuitem" type="button" @click="emitDelete(event)">
                <i class="fas fa-trash"></i><span>Delete</span>
              </button>
            </template>
          </div>

          <teleport to="body">
            <div
              v-if="openId === event.id && isCompactScreen"
              class="invite-sheet-overlay"
              @click="closeMenu"
            >
              <div class="invite-sheet" role="menu" tabindex="-1" @click.stop>
                <span class="invite-sheet__handle" aria-hidden="true"></span>
                <div class="invite-sheet__body">
                  <template v-if="sectionType === 'active' || sectionType === 'upcoming'">
                    <router-link :to="`/cohost/${event.id}`" v-if="event.cohost_add_status == 1" class="dropdown-item" role="menuitem" @click="closeMenu">
                      <i class="fas fa-user-tie"></i><span>Add Co-Host</span>
                    </router-link>
                    <router-link :to="`/guest/${event.id}`" class="dropdown-item" role="menuitem" @click="closeMenu">
                      <i class="fas fa-users"></i><span>Add Guests</span>
                    </router-link>
                    <router-link :to="`/edit-invitation/${event.id}`" class="dropdown-item" role="menuitem" @click="closeMenu">
                      <i class="fas fa-edit"></i><span>Edit Invitation</span>
                    </router-link>
                  </template>

                  <template v-else-if="sectionType === 'past' || sectionType === 'draft'">
                    <router-link :to="`/edit-invitation/${event.id}`" class="dropdown-item" v-if="sectionType === 'draft' && event.edit_option == 'yes'" role="menuitem" @click="closeMenu">
                      <i class="fas fa-edit"></i><span>Edit Invitation</span>
                    </router-link>
                  </template>

                  <router-link 
                    v-if="event.event_type === 'old'" 
                    :to="`/guest/${event.id}`" 
                    class="dropdown-item" 
                    role="menuitem" 
                    @click="closeMenu"
                  >
                    <i class="fas fa-users"></i><span>Add Guests</span>
                  </router-link>

                  <router-link v-if="sectionType !== 'past'" :to="`/send-enquiry/${event.id}`" class="dropdown-item" role="menuitem" @click="closeMenu">
                    <i class="fas fa-paper-plane"></i><span>Send Enquiry</span>
                  </router-link>

                  <router-link
                    v-if="shouldShowFeedback(event)"
                    :to="`/feedbacks/${event.id}`"
                    class="dropdown-item"
                    role="menuitem"
                    @click="closeMenu"
                  >
                    <i class="fas fa-comment-alt"></i><span>Feedbacks</span>
                  </router-link>

                  <button class="dropdown-item" role="menuitem" type="button" @click="openShareVia(event)">
                    <i class="far fa-share-square"></i><span>Share Via</span>
                  </button>
                  <button class="dropdown-item" role="menuitem" type="button" @click="openShareQr(event)">
                    <i class="fas fa-qrcode"></i><span>Album QR Code</span>
                  </button>

                  <div class="dropdown-divider" v-if="canDeleteEvent(event)"></div>

                  <button class="dropdown-item text-danger" role="menuitem" type="button" v-if="canDeleteEvent(event)" @click="emitDelete(event)">
                    <i class="fas fa-trash"></i><span>Delete</span>
                  </button>
                </div>
              </div>
            </div>
          </teleport>
        </div>
      </div>

      <router-link
        :to="`/detail/${event.id}`"
        class="invite-card__body"
        @click="closeMenu"
      >
        <div class="invite-card__media">
          <div class="invite-card__media-bg" :style="mediaBgStyle(event)"></div>
          <img
            class="invite-card__media-img"
            :src="getPhoto(event)"
            :alt="`${readableTitle(event)} cover`"
            loading="lazy"
            @error="onImgError"
          />
          <span v-if="hasVideoMedia(event)" class="invite-card__play-overlay" aria-hidden="true">
            <i class="fas fa-play"></i>
          </span>
        </div>
        <div class="invite-card__details">
          <div class="invite-card__chips">
            <span class="invite-chip">{{ sectionChipLabel }}</span>
            <span v-if="event.host_name" class="invite-chip invite-chip--muted">Co-Host</span>
            <span v-else-if="event.hostname" class="invite-chip invite-chip--muted">Hosted</span>
          </div>

          <div class="meta-row">
            <span class="emoji-icon" aria-hidden="true">📅</span>
            <span class="text-muted">{{ eventRangeText(event) }}</span>
          </div>

          <div class="meta-row" v-if="event.event_place || event.door_no || event.address">
            <span class="emoji-icon" aria-hidden="true">📍</span>
            <span class="text-muted text-truncate-2">
              <template v-if="event.event_place">{{ event.event_place }}</template>
              <template v-if="event.door_no">, {{ event.door_no }}</template>
              <template v-if="event.address">, {{ event.address }}</template>
            </span>
          </div>

          <div class="host-row" v-if="event.hostname">
            <i class="far fa-user"></i>
            <span class="text-muted">Host: <strong>{{ event.hostname }}</strong></span>
          </div>
        </div>
      </router-link>
    </article>
  </div>

  <!-- Share Via Modal -->
  <ShareViaModal
    :visible="showShareVia"
    :title="shareTitle"
    :image="shareImage"
    :description="shareDescription"
    :dates="shareDates"
    :host="shareHost"
    :location="shareLocation"
    :address="shareAddress"
    :map-url="shareMapUrl"
    :url="shareUrl"
    @close="showShareVia = false"
  />

  <!-- QR Share Modal -->
  <QrShareModal
    :visible="showQrModal"
    :qrSrc="shareQrImage"
    :url="shareQrUrl"
    @close="showQrModal = false"
  />
</template>

 
<script setup>
/* eslint-env vue/setup-compiler-macros */
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import ShareViaModal from '@/components/common/ShareViaModal.vue'
import QrShareModal from '@/components/common/QrShareModal.vue'
import { formatEventDateRange } from '@/utils/date'

const props = defineProps({
  title: { type: String, default: '' },
  items: { type: Array, default: () => [] },
  type:  { type: String, default: '' }
})
const emit = defineEmits(['delete'])

/* Section type */
const sectionType = computed(() => {
  if (props.type) return props.type
  const t = (props.title || '').toLowerCase()
  if (t.includes('active'))   return 'active'
  if (t.includes('upcoming')) return 'upcoming'
  if (t.includes('past'))     return 'past'
  if (t.includes('draft'))    return 'draft'
  return 'active'
})

const sectionChipLabel = computed(() => {
  if (sectionType.value === 'active') return 'Active'
  if (sectionType.value === 'upcoming') return 'Upcoming'
  if (sectionType.value === 'past') return 'Past'
  if (sectionType.value === 'draft') return 'Draft'
  return 'Invitation'
})

/* Dropdown state */
const openId = ref(null)
const isCompactScreen = ref(false)
const componentId = `inv-list-${Math.random().toString(36).slice(2)}`

function toggleMenu(id) {
  if (openId.value === id) {
    closeMenu()
  } else {
    openId.value = id
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('invitation-dropdown-open', { detail: { sourceId: componentId } }))
    }
  }
}

function closeMenu(){
  openId.value = null
}

const updateViewportFlag = () => {
  if (typeof window === 'undefined') return
  isCompactScreen.value = window.matchMedia('(max-width: 992px)').matches
}

const onExternalDropdown = (e) => {
  if (e?.detail?.sourceId && e.detail.sourceId !== componentId) {
    closeMenu()
  }
}

onMounted(() => {
  updateViewportFlag()
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', updateViewportFlag)
    window.addEventListener('invitation-dropdown-open', onExternalDropdown)
  }
})

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updateViewportFlag)
    window.removeEventListener('invitation-dropdown-open', onExternalDropdown)
  }
})

/* ---------- FIX 2: guard against same-tick close ---------- */
const onGlobalClick = (e) => {
  if (!openId.value) return
  const dropdownEl = e.target.closest('.invite-dropdown')
  if (dropdownEl && String(dropdownEl.dataset.id) === String(openId.value)) return
  closeMenu()
}

const onEsc = (e) => { if (e.key === 'Escape') closeMenu() }

onMounted(() => {
  document.addEventListener('click', onGlobalClick)
  document.addEventListener('keydown', onEsc)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onGlobalClick)
  document.removeEventListener('keydown', onEsc)
})

/* Emits */
const emitDelete = (event) => { emit('delete', event); closeMenu() }

/* Helpers */
function readableTitle(e){ return e?.orgname || e?.eventname || e?.title || 'Event' }

const FALLBACK_IMG = 'https://dummyimage.com/300x300/e9eef6/6c7a92.png&text=Event'
function hasVideoMedia(event) {
  if (!event) return false
  const photoType = String(event.photo_type || '').trim().toLowerCase()
  return photoType === 'video'
}
function getPhoto(event){
  if (!event) return FALLBACK_IMG
  const candidates = [event.photo, event.video_thumb, event.eventphoto, event.type_image, event.cover, event.image]
  const chosen = candidates.find(u => u && String(u) !== '0' && u !== 'null')
  return chosen ? String(chosen).trim() : FALLBACK_IMG
}
function mediaBgStyle (event) {
  const src = getPhoto(event)
  return { backgroundImage: `url(${src})` }
}
function onImgError(ev){ ev.target.src = FALLBACK_IMG; ev.target.onerror = null }
function eventRangeText(event = {}) {
  const text = formatEventDateRange({
    fromDate: event.fromdate,
    toDate: event.todate,
    fromTime: event.fromtime,
    toTime: event.totime
  })
  return text || 'Date not specified'
}
function truncate(str, max=60){ return !str ? '' : (str.length > max ? str.slice(0, max) + '…' : str) }

function isTruthyFlag(val) {
  const s = String(val ?? '').trim().toLowerCase()
  return s === '1' || s === 'yes' || s === 'true'
}

function shouldShowFeedback(event = {}) {
  return isTruthyFlag(event.feedback) || isTruthyFlag(event.feedbacks)
}

function canDeleteEvent(event = {}) {
  if (String(event.delete_option) !== 'yes') return false
  return sectionType.value === 'active' || sectionType.value === 'past' || sectionType.value === 'draft'
}

/* Share state */
const showShareVia = ref(false)
const showQrModal  = ref(false)
const shareEvent   = ref(null)

function openShareVia(event){ shareEvent.value = event; showShareVia.value = true; closeMenu() }
function openShareQr(event) { shareEvent.value = event; showQrModal.value = true;  closeMenu() }

const shareTitle = computed(() => readableTitle(shareEvent.value || {}))
const shareImage = computed(() => getPhoto(shareEvent.value))
const shareDescription = computed(() => (shareEvent.value?.description || shareEvent.value?.event_description || ''))
const shareHost = computed(() => (shareEvent.value?.hostname || shareEvent.value?.host_name || shareEvent.value?.host || ''))
const shareDates = computed(() => {
  const e = shareEvent.value || {}
  return formatEventDateRange({
    fromDate: e.fromdate,
    toDate: e.todate,
    fromTime: e.fromtime,
    toTime: e.totime
  })
})
const shareUrl     = computed(() => (shareEvent.value?.share_url || shareEvent.value?.share_link || ''))
const shareLocation = computed(() => shareEvent.value?.event_place || '')
const shareAddress = computed(() => {
  const e = shareEvent.value || {}
  const parts = [e.event_place, e.door_no, e.address].filter(Boolean)
  return parts.join(', ')
})
const shareMapUrl = computed(() => {
  const e = shareEvent.value || {}
  if (e.map_url) return e.map_url
  if (e.lat && e.lng) return `https://www.google.com/maps?q=${encodeURIComponent(e.lat)},${encodeURIComponent(e.lng)}`
  if (shareAddress.value) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(shareAddress.value)}`
  return ''
})
const shareQrImage = computed(() => (shareEvent.value?.qrcode_image || ''))
const shareQrUrl   = computed(() => (shareEvent.value?.qr_url || ''))
</script>


<style scoped>
.invite-card {
  position: relative;
  border-radius: 22px;
  padding: 1rem 1rem 0.95rem;
  background: linear-gradient(180deg, #fff8fb 0%, #fff2f7 100%);
  border: 1px solid #f2d5df;
  box-shadow: 0 10px 24px rgba(242, 172, 196, 0.14);
  margin-bottom: 1.3rem;
  transition: transform .18s ease, box-shadow .18s ease;
  overflow: visible;
  z-index: 0;
}
.invite-card > * {
  position: relative;
  z-index: 1;
}
.invite-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 18px 36px rgba(242, 172, 196, 0.2);
}

.invite-card.dropdown-open {
  z-index: 30;
}

.invitation-group__head{
  padding: 2px 2px 0;
}

.invitation-group__title{
  color:#2a1b22;
  font-size:1.02rem;
  font-weight:700;
}

.invite-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: nowrap;
}
.invite-card__header > div:first-child {
  flex: 1;
  min-width: 0;
}
.invite-card__header .invite-dropdown {
  flex-shrink: 0;
}

.invite-card__body {
  display: grid;
  grid-template-columns: 104px 1fr;
  gap: 1rem;
  margin-top: .85rem;
  color: inherit;
  text-decoration: none;
  align-items: stretch;
}

.invite-card__media {
  border-radius: 18px;
  overflow: hidden;
  height: 104px;
  width: 104px;
  background: linear-gradient(180deg, #fff6fa 0%, #ffeaf3 100%);
  border: 1px solid #f4cfdb;
  position: relative;
}
.invite-card__media-bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  filter: blur(26px) saturate(1.15);
  transform: scale(1.08);
  opacity: 0;
  transition: opacity .2s ease;
}
.invite-card__media::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(58, 19, 36, .42), rgba(58, 19, 36, .08));
  opacity: 0;
  transition: opacity .2s ease;
  z-index: 0;
}
.invite-card__media-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  position: relative;
  z-index: 1;
}
.invite-card__play-overlay{
  position:absolute;
  inset:0;
  display:flex;
  align-items:center;
  justify-content:center;
  z-index:2;
  pointer-events:none;
}
.invite-card__play-overlay i{
  width:34px;
  height:34px;
  border-radius:50%;
  background:linear-gradient(135deg, #ff5a93 0%, #ff8f57 100%);
  color:#ffffff;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:13px;
  box-shadow:0 8px 18px rgba(15,23,42,.28);
}

.badge-soft {
  background: #fff4f8;
  color: #b8326d;
  font-weight: 700;
  padding: .34rem .62rem;
  border-radius: 999px;
  font-size: .75rem;
  border: 1px solid #f0c8d7;
}

.eyebrow { font-size: .8rem; color: #8c6f7c; }
.event-title { font-size: 1.08rem; font-weight: 700; color: #2b1b22; }
.invite-card__chips{
  display:flex;
  flex-wrap:wrap;
  gap:8px;
  margin-bottom:.4rem;
}
.invite-chip{
  display:inline-flex;
  align-items:center;
  border:1px solid #f0c8d7;
  background:#fff;
  color:#ec4899;
  border-radius:999px;
  padding:4px 10px;
  font-size:.78rem;
  font-weight:700;
}
.invite-chip--muted{
  color:#9f6480;
}
.meta-row, .host-row {
  display: flex;
  align-items: flex-start;
  gap: .5rem;
  color: #57424c;
  font-size: .9rem;
  margin-top: .32rem;
}
.emoji-icon{
  display:inline-block;
  line-height:1;
}
.meta-row .emoji-icon,
.host-row .emoji-icon {
  margin-top: .1rem;
  display: inline-block;
  line-height: 1;
}
.text-truncate-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.invite-card__body:hover .event-title { text-decoration: none; }

.dropdown {
  position: relative;
  z-index: 5;
}
.dropbutton {
  height: 34px;
  width: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #f1d7e1;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 6px 16px rgba(236,72,153,.08);
}
.dropbutton i { font-size: 13px; color: #654654; }

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 220px;
  max-width: 320px;
  max-height: 60vh;
  overflow-y: auto;
  padding: .35rem 0;
  border: 1px solid #f3d4df;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 14px 30px rgba(86, 42, 62, 0.16);
  z-index: 10;
}
.dropdown-menu::after {
  content: '';
  position: absolute;
  top: -8px;
  right: 12px;
  width: 18px;
  height: 18px;
  background: #fff;
  border: 1px solid #f3d4df;
  border-right: none;
  border-bottom: none;
  transform: rotate(45deg);
  transform-origin: center;
  box-shadow: -2px -2px 6px rgba(15,23,42,0.05);
}
.dropdown-menu::-webkit-scrollbar { width: 8px; }
.dropdown-menu::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }

.invite-sheet-overlay {
  position: fixed;
  inset: 0;
  background: rgba(27, 18, 24, 0.25);
  backdrop-filter: blur(2px);
  z-index: 1050;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0 12px max(16px, env(safe-area-inset-bottom, 16px));
}

.invite-sheet {
  width: min(420px, 100%);
  background: rgba(255, 255, 255, 0.98);
  border-radius: 24px;
  box-shadow: 0 -20px 60px rgba(15, 23, 42, 0.18);
  padding-bottom: max(8px, env(safe-area-inset-bottom, 8px));
}

.invite-sheet__handle {
  width: 48px;
  height: 5px;
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.6);
  display: block;
  margin: 8px auto 4px;
}

.invite-sheet__body {
  max-height: 70vh;
  overflow-y: auto;
  padding: 4px 0 6px;
}
.invite-sheet__body .dropdown-item {
  font-size: 1rem;
  padding: 0.7rem 1.1rem;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: .6rem;
  width: 100%;
  padding: .48rem .9rem;
  font-size: .92rem;
  color: #3b2230;
  background: transparent;
  border: 0;
  text-align: left;
  cursor: pointer;
}
.dropdown-item i {
  width: 18px;
  text-align: center;
  color: #654654;
}
.dropdown-item:hover { background: #fff4f8; }
.dropdown-divider { margin: .35rem 0; }
.dropdown-item.text-danger i { color: #dc2626; }

@media (max-width: 992px) {
  .invite-card__body {
    grid-template-columns: 1fr;
  }
  .invite-card__media {
    height: 220px;
    width: 100%;
  }
  .invite-card__media-bg,
  .invite-card__media::after {
    opacity: 1;
  }
  .invite-card__media-img {
    object-fit: contain;
    padding: 12px;
  }
}
@media (max-width: 768px) {
  .invite-card {
    padding: 1rem;
  }
  .invite-card__media {
    height: 190px;
  }
  .invite-card__media-img {
    padding: 10px;
  }
}
@media (max-width: 576px) {
  .invitation-group__head{
    margin-bottom:.85rem !important;
  }
  .event-title{
    font-size:1rem;
  }
  .dropdown-menu {
    min-width: 220px;
    max-width: calc(100vw - 24px);
    right: 0;
  }
  .invite-card__media {
    height: 170px;
  }
  .event-title { font-size: 1rem; }
  .dropbutton { height: 34px; width: 34px; }
}
</style>
